import { Divider, Grid, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { createInvoicePageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { isNotNullOrUndefined, vyDate } from '@vy/utils';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useFormErrorNotificationContext } from '../../../../contexts/form-error-notification-context/form-error-notification-context';
import { vyFormRegister } from '../../../../utils/react-hook-forms-utils';
import { useCreditNoteOptions } from '../create-credit-note-page-hooks/useCreditNoteOptions';
import { ControlledAutocomplete } from './ControlledAutocomplete';
import type { CreateCreditNoteFormValues } from './CreateCreditNoteForm/CreateCreditNoteForm';
import { useActivatedIntegration } from '../../../../hooks/use-activated-integration';
import { INTEGRATION_PROVIDERS } from '@vy/types';

export const CreateCreditNoteSettings: React.FC = () => {
  const { control, register, setValue } = useFormContext<CreateCreditNoteFormValues>();
  const { registerErrorField } = useFormErrorNotificationContext();
  const isNetSuiteEnabled = useActivatedIntegration(INTEGRATION_PROVIDERS.netSuite);
  const isQuickBooksEnabled = useActivatedIntegration(INTEGRATION_PROVIDERS.quickBooks);
  const isXeroEnabled = useActivatedIntegration(INTEGRATION_PROVIDERS.xero);
  const {
    customersOptions,
    contractsOptions,
    invoicesOptions,
    netSuiteProductsOptions,
    quickBooksProductsOptions,
    xeroProductsOptions,
  } = useCreditNoteOptions();

  const customerIdRegister = register('customerId');
  const amountRegister = register('amount');

  registerErrorField(customerIdRegister.name);
  registerErrorField(amountRegister.name);

  return (
    <section className="flex h-full flex-col p-6 gap-y-6 overflow-auto">
      <Typography variant="h6">Credit Note Settings</Typography>
      <Stack direction="column" sx={{
        mb: "40px"
      }}>
        <Typography variant="overline" sx={{
          color: "text.secondary"
        }}>
          Credit Grant
        </Typography>
        <Stack
          direction="column"
          component={Paper}
          elevation={1}
          sx={{
            mb: "40px",
            padding: 3
          }}>
          <Grid container sx={{
            alignItems: "center"
          }}>
            <Grid sx={{
              flex: 1
            }}>
              <Typography>Amount</Typography>
            </Grid>

            <Grid size={3}>
              <TextField
                size="small"
                type="number"
                placeholder="Amount"
                fullWidth
                {...vyFormRegister(register, 'amount', { valueAsNumber: true })}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography sx={{
                          color: "text.secondary"
                        }}>$</Typography>
                      </InputAdornment>
                    ),
                  },

                  htmlInput: { min: 0, step: 0.01 }
                }} />
            </Grid>
          </Grid>
        </Stack>

        <Typography variant="overline" sx={{
          color: "text.secondary"
        }}>
          Recipient
        </Typography>

        <Stack
          component={Paper}
          elevation={1}
          divider={<Divider />}
          sx={{
            rowGap: 3,
            padding: 3
          }}>
          <Grid container sx={{
            alignItems: "center"
          }}>
            <Grid sx={{
              flex: 1
            }}>
              <Typography>Customer</Typography>
            </Grid>

            <Grid size={3}>
              <ControlledAutocomplete
                name={customerIdRegister.name}
                control={control}
                options={customersOptions}
                selectorAttribute={getSelectorAttribute(createInvoicePageSelectors.settings.customerSelect)}
              />
            </Grid>
          </Grid>

          <Grid container sx={{
            alignItems: "center"
          }}>
            <Grid sx={{
              flex: 1
            }}>
              <Typography>Contract</Typography>
            </Grid>

            <Grid size={3}>
              <ControlledAutocomplete name="contractId" control={control} options={contractsOptions} />
            </Grid>
          </Grid>

          <Grid container sx={{
            alignItems: "center"
          }}>
            <Grid sx={{
              flex: 1
            }}>
              <Typography>Original Invoice</Typography>
            </Grid>
            <Grid size={3}>
              <ControlledAutocomplete name="invoiceId" control={control} options={invoicesOptions} />
            </Grid>
          </Grid>

          {isNetSuiteEnabled && (
            <Grid container sx={{
              alignItems: "center"
            }}>
              <Grid sx={{
                flex: 1
              }}>
                <Typography>NetSuite Product</Typography>
              </Grid>
              <Grid size={3}>
                <ControlledAutocomplete
                  name="netSuiteItemId"
                  control={control}
                  options={netSuiteProductsOptions}
                />
              </Grid>
            </Grid>
          )}

          {isQuickBooksEnabled && (
            <Grid container sx={{
              alignItems: "center"
            }}>
              <Grid sx={{
                flex: 1
              }}>
                <Typography>QuickBooks Product</Typography>
              </Grid>
              <Grid size={3}>
                <ControlledAutocomplete
                  name="quickBooksItemId"
                  control={control}
                  options={quickBooksProductsOptions}
                />
              </Grid>
            </Grid>
          )}

          {isXeroEnabled && (
            <Grid container sx={{
              alignItems: "center"
            }}>
              <Grid sx={{
                flex: 1
              }}>
                <Typography>Xero Product</Typography>
              </Grid>
              <Grid size={3}>
                <ControlledAutocomplete
                  name="xeroItemCode"
                  control={control}
                  options={xeroProductsOptions}
                />
              </Grid>
            </Grid>
          )}

          <Grid container sx={{
            alignItems: "center"
          }}>
            <Grid sx={{
              flex: 1
            }}>
              <Typography>Date</Typography>
            </Grid>

            <Grid size={3}>
              <Controller
                name="date"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    value={vyDate(field.value)}
                    onChange={(newDate) => {
                      if (isNotNullOrUndefined(newDate)) {
                        const startOfNewDate = vyDate(newDate).startOf('day').toDate();
                        setValue('date', startOfNewDate);
                      }
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        borderRadius: '10px',
                        '& fieldset': {
                          borderColor: '#E8F2F8',
                        },
                        '&:hover fieldset': {
                          borderColor: '#E8F2F8',
                        },
                        '&.Mui-focused fieldset': {
                          border: '1px solid #E8F2F8',
                        },
                      },
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Stack>
      </Stack>
    </section>
  );
};
